// Adjudication rubrics, keyed by competition year. Same idea as seasons.js:
// a new year is a new entry, no other code changes.
//
// The report page (src/pages/Report.js) reads the rubric for a report's
// competition_year and renders whatever it finds — N categories on the radar
// and scorecard, N feedback sections. `judging_reports.scores` in the DB is
// keyed by these category keys; `judging_reports.feedback` by the section keys.
//
// If a year needs different rubrics per performance category (e.g. competitive
// vs. recreational), add a `byPerformanceCategory` map on that year's entry and
// extend getRubric() to consult it. Not needed for 2026.

export const RUBRICS = {
  2026: {
    name: 'Classical Variation 2026',
    scaleMax: 10,        // each category scored 1..scaleMax per judge
    // How many places get a badge, by size of the ranking group (dancers in
    // the same year + age division + performance category). First tier whose
    // maxGroupSize >= the group size wins. A dancer with two variations in
    // one group is ranked once, on their better variation (see the ranking
    // SQL in database/2026-09-13-judging-reports.sql).
    // Tuned to what was actually announced on the day in 2026, not derived
    // from a rule: Adult Competitive had 7 dancers and 3 placements (hence the
    // 7 boundary), and 12-14 had 12 dancers and 5 placements.
    rankBadgeTiers: [
      { maxGroupSize: 3,        topN: 1  },
      { maxGroupSize: 7,        topN: 3  },
      { maxGroupSize: 15,       topN: 5  },
      { maxGroupSize: Infinity, topN: 10 },
    ],

    // Below this overall percentage the coloured score band is hidden on the
    // report. The lowest bands read as a verdict — a seven-year-old does not
    // need "Limited · 47%" in red at the top of the page. The score itself,
    // the breakdown and the judges' feedback all still show; only the pill
    // that labels the child is withheld.
    hideBandBelowPct: 50,
    categories: [
      {
        key: 'foundation',
        label: 'Ballet Foundation',
        shortLabel: 'Foundation',
        weight: 1,
        description:
          'The quality and consistency of the dancer\'s basic ballet training — classical positions, turnout, pointed feet, port de bras, épaulement, and overall cleanliness of technique.',
      },
      {
        key: 'technique',
        label: 'Ballet Technique Application',
        shortLabel: 'Technique',
        weight: 1,
        description:
          'How correctly the dancer applies ballet technique to turns, jumps, and other movements. Judged on form, control, and understanding — not on the difficulty of the elements.',
      },
      {
        key: 'artistic',
        label: 'Artistic Presentation & Character',
        shortLabel: 'Artistic',
        weight: 2,
        description:
          'Artistic interpretation, musicality, character, expression, stage presence, and how the dancer communicates the variation. Weighted ×2 at final tabulation.',
      },
    ],
    feedbackSections: [
      { key: 'strengths',    heading: 'Strengths' },
      { key: 'improvements', heading: 'Areas to Improve' },
    ],
    // Band for the overall 0..scaleMax average. First match wins (descending).
    scoreBands: [
      { min: 9, label: 'Excellent',         color: '#2e7d32' },
      { min: 7, label: 'Strong',            color: 'var(--primary-gold)' },
      { min: 5, label: 'Developing',        color: '#c78500' },
      { min: 3, label: 'Limited',           color: '#c94a3a' },
      { min: 0, label: 'Needs Development', color: '#8a2323' },
    ],
    // Compact legend strip at the bottom of the report.
    scoreReference: [
      { range: '9–10', label: 'Excellent' },
      { range: '7–8',  label: 'Strong' },
      { range: '5–6',  label: 'Developing' },
      { range: '3–4',  label: 'Limited' },
      { range: '1–2',  label: 'Needs Dev.' },
    ],
  },
};

export function getRubric(year) {
  return RUBRICS[year] || null;
}

/**
 * Number of places that earn a badge for a group of `groupSize` dancers.
 * Falls back to the largest tier when the size is unknown.
 */
export function getRankBadgeTopN(rubric, groupSize) {
  const tiers = rubric?.rankBadgeTiers || [];
  if (tiers.length === 0) return 0;
  if (typeof groupSize !== 'number' || groupSize < 1) return tiers[tiers.length - 1].topN;
  return (tiers.find((t) => groupSize <= t.maxGroupSize) || tiers[tiers.length - 1]).topN;
}

/** True when a report row's rank earns a badge under the rubric's tiers. */
export function earnsRankBadge(rubric, report) {
  const rank = report?.rank;
  if (typeof rank !== 'number' || rank < 1) return false;
  return rank <= getRankBadgeTopN(rubric, report.rank_group_size);
}

/**
 * Derive everything the report needs from a judging_reports row + its rubric.
 * Pure function so the page and any future email/PDF renderer agree.
 *
 * scores: { [judgeKey]: { [categoryKey]: number } }
 */
export function tabulate(scores, rubric) {
  const judges = Object.keys(scores || {}).sort();
  const rows = rubric.categories.map((cat) => {
    const perJudge = judges.map((j) => Number(scores[j]?.[cat.key] ?? 0));
    const combined = perJudge.reduce((a, b) => a + b, 0);
    return {
      cat,
      perJudge,
      combined,
      combinedMax: judges.length * rubric.scaleMax,
      average: judges.length ? combined / judges.length : 0,
      final: combined * cat.weight,
    };
  });
  const weightSum = rubric.categories.reduce((acc, c) => acc + c.weight, 0);
  const totalFinal = rows.reduce((acc, r) => acc + r.final, 0);
  const totalMax = judges.length * rubric.scaleMax * weightSum;
  const overallAvg = judges.length ? totalFinal / (judges.length * weightSum) : 0;
  const band = rubric.scoreBands.find((b) => overallAvg >= b.min) || rubric.scoreBands[rubric.scoreBands.length - 1];
  return { judges, rows, totalFinal, totalMax, overallAvg, overallPct: totalMax ? (totalFinal / totalMax) * 100 : 0, band };
}
